import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

export const OfferModal = ({ open, onOpenChange, product }) => {
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/offers", { product_id: product.id, amount: parseFloat(amount), message });
      setDone(true);
      toast.success("Your offer has been submitted privately.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit offer.");
    } finally {
      setBusy(false);
    }
  };

  const close = (v) => { onOpenChange(v); if (!v) { setDone(false); setAmount(""); setMessage(""); } };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="offer-modal" className="sm:max-w-[440px] rounded-none border border-white/15 bg-[#0a0a0a] p-0 text-[#f5f5f0]">
        <VisuallyHidden>
          <DialogTitle>Make a private offer</DialogTitle>
          <DialogDescription>Submit a discreet offer for this piece.</DialogDescription>
        </VisuallyHidden>
        <div className="p-10">
          {done ? (
            <div className="text-center">
              <p className="font-sans-lux text-[10px] uppercase tracking-[0.4em] text-gold">Received</p>
              <h2 className="font-serif-lux mt-4 text-4xl font-light">Offer submitted</h2>
              <p className="mt-4 text-sm font-light text-[#a1a1aa]">
                Our curator will review your offer privately and respond by email.
              </p>
              <button data-testid="offer-close-btn" onClick={() => close(false)}
                className="hover-invert mt-8 w-full border border-[#f5f5f0] px-6 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-[#f5f5f0] hover:text-black">
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="font-sans-lux text-[10px] uppercase tracking-[0.4em] text-gold">Private offer</p>
              <h2 className="font-serif-lux mt-4 text-3xl font-light leading-none">{product.title}</h2>
              <p className="mt-3 text-sm font-light text-[#a1a1aa]">List price {money(product.price, product.currency)}</p>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">Your offer (USD)</label>
                  <input data-testid="offer-amount-input" type="number" min="1" required value={amount}
                    onChange={(e) => setAmount(e.target.value)} placeholder="0"
                    className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-lg outline-none focus:border-gold" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">Message (optional)</label>
                  <textarea data-testid="offer-message-input" rows={3} value={message}
                    onChange={(e) => setMessage(e.target.value)} placeholder="A note to the curator…"
                    className="mt-2 w-full resize-none border border-white/20 bg-transparent p-3 text-sm outline-none focus:border-gold" />
                </div>
                <button data-testid="offer-submit-btn" type="submit" disabled={busy}
                  className="hover-invert w-full border border-[#f5f5f0] bg-[#f5f5f0] px-6 py-4 text-[11px] uppercase tracking-[0.3em] text-black hover:bg-transparent hover:text-[#f5f5f0] disabled:opacity-50">
                  {busy ? "Submitting…" : "Submit private offer"}
                </button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
